import React, { useState } from "react";
import ChatWindow from "./components/ChatWindow";

/**
 * Example page demonstrating how to use the ChatWindow component
 * Replace the placeholder values with your actual workflow ID and client secret
 */
const ChatAgentPage: React.FC = () => {
  const [showChat, setShowChat] = useState(false);
  const [workflowId, setWorkflowId] = useState(
    "wf_68e650111e3481909390d49ce8f1da200c76078071fdb14c"
  );
  const [clientSecret, setClientSecret] = useState(
    "ek_68eae07c8e8c81908ae21139c949f0fa0e9abd596b345d77_00eyJleHBpcmVzX2F0IjogMTc2MDIyMzk1Nn0="
  );

  const isValid = workflowId.trim() !== "" && clientSecret.trim() !== "";

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">
          Chat with AI Agent (ChatKit)
        </h1>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-700 mb-4">
            Configuration
          </h2>

          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workflow ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={workflowId}
                onChange={(e) => setWorkflowId(e.target.value)}
                placeholder="Paste your workflow ID (e.g., wf_xxxxx)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2f4941] focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Get this from OpenAI Platform → ChatKit → Your Workflow
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Secret <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                placeholder="Paste your client secret (e.g., cs_xxxxx)"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#2f4941] focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500">
                Keep this secret! Don't share or commit to Git
              </p>
            </div>
          </div>

          <button
            onClick={() => setShowChat(true)}
            className="px-6 py-3 bg-[#2f4941] text-white rounded-lg hover:bg-[#3d5f54] transition-colors shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!isValid}
          >
            Open Chat Window
          </button>

          {!isValid && (
            <p className="mt-4 text-sm text-amber-600">
              ℹ️ Please enter both Workflow ID and Client Secret above to start
              chatting
            </p>
          )}
        </div>

        {/* Usage Instructions */}
        <div className="mt-6 bg-blue-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">
            How to Use
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-blue-800">
            <li>
              Go to{" "}
              <a
                href="https://platform.openai.com"
                target="_blank"
                rel="noopener noreferrer"
                className="underline hover:text-blue-600"
              >
                OpenAI Platform
              </a>
            </li>
            <li>Navigate to ChatKit section</li>
            <li>Create or select a workflow</li>
            <li>Copy your Workflow ID and Client Secret</li>
            <li>Paste them in the input fields above</li>
            <li>Click "Open Chat Window" to start chatting</li>
          </ol>
        </div>

        {/* Integration Example */}
        <div className="mt-6 bg-green-50 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-green-900 mb-3">
            Integration Example
          </h3>
          <pre className="bg-green-900 text-green-50 p-4 rounded-md overflow-x-auto text-sm">
            {`import { ChatWindow } from '@/features/chat-with-agents';

// In your component:
<ChatWindow
  workflowId="your-workflow-id"
  clientSecret="your-client-secret"
  agentName="Marketing Agent"
  onClose={() => setShowChat(false)}
/>`}
          </pre>
        </div>
      </div>

      {/* Chat Window Modal */}
      {showChat && isValid && (
        <ChatWindow
          idToken={clientSecret}
          agentName="Demo Agent"
          onClose={() => setShowChat(false)}
        />
      )}
    </div>
  );
};

export default ChatAgentPage;
